import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck, ListChecks, Info } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n/LanguageContext";
import { TUMOR_GUIDE, TREATMENTS } from "../content/siteContent";
import { TUMOR_PAGES } from "../content/tumors";
import { locT, locTumor } from "../content/i18n_extra";
import { SpeakButton } from "../components/SpeakButton";
import { SEO } from "../components/SEO";

/* Kanser türü SEO sayfası — /kanser/:id. Tümör rehberi çipleri, dergi ve Tudi buraya bağlanır. */
export default function TumorDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const guide = TUMOR_GUIDE.find((g) => g.id === id);
  const page = TUMOR_PAGES[id];

  if (!guide || !page) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-40 pb-24 text-center px-6">
          <p className="font-heading text-2xl font-bold text-ink">{t("sec_guide_title")}</p>
          <p className="mt-3 text-sm text-sub">{t("tp_notfound")}</p>
          <Link to="/#rehber" className="mt-6 inline-flex items-center gap-2 bg-brand hover:bg-branddeep text-white font-semibold text-sm px-6 py-3 rounded-full transition-colors">
            {t("sec_guide_title")} →
          </Link>
        </div>
      </div>
    );
  }

  const c = page[lang] || page.en;
  const name = locTumor(guide, lang);
  const recommended = guide.treatments.map((tid) => TREATMENTS.find((x) => x.id === tid)).filter(Boolean);
  const speakText = `${c.title}. ${c.intro} ${c.bullets.join(" ")} ${c.note}`;

  const statusStyle = {
    approved: { backgroundColor: "rgba(120,168,137,0.14)", color: "#5d8a6d", dot: "#78A889" },
    research: { backgroundColor: "rgba(201,169,106,0.16)", color: "#8A6B2F", dot: "#C9A96A" },
    free: { backgroundColor: "rgba(198,74,59,0.10)", color: "#C64A3B", dot: "#C64A3B" },
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`tumor-detail-${id}`}>
      <SEO
        title={`${c.title} — ${t("tp_seo_suffix")}`}
        description={c.intro.slice(0, 155)}
        path={`/kanser/${id}`}
        image="/og-cover.jpg"
        type="article"
        keywords={`${c.title}, ${name}, ${t("tp_seo_suffix")}, japonyada kanser tedavisi, cancer treatment japan, 2d healthcare, i-cell networks`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: `${c.title} — 2D Healthcare`,
            description: c.intro,
            about: { "@type": "MedicalCondition", name: name },
            medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
            inLanguage: lang,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "2D Healthcare", item: "https://japonyadakansertedavisi.com/" },
              { "@type": "ListItem", position: 2, name: t("sec_guide_title"), item: "https://japonyadakansertedavisi.com/#rehber" },
              { "@type": "ListItem", position: 3, name: c.title, item: `https://japonyadakansertedavisi.com/kanser/${id}` },
            ],
          },
        ]}
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-ice overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl bg-brand/10" />
        <div className="relative max-w-5xl mx-auto px-6">
          <Link to="/#rehber" data-testid="tumor-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sub hover:text-brand transition-colors">
            <ArrowLeft size={15} /> {t("sec_guide_title")}
          </Link>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-6">
            <span className="inline-block text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full bg-brand/10 text-brand">{t("tp_kicker")}</span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{c.title}</h1>
            <p className="mt-5 text-base sm:text-lg text-sub leading-relaxed max-w-3xl">{c.intro}</p>
            <div className="mt-5">
              <SpeakButton getText={() => speakText} label={t("mag_listen")} testid="tumor-speak-button" />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-[1fr_320px] gap-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="flex items-center gap-2.5 font-heading text-2xl font-bold text-ink">
              <ListChecks size={20} className="text-brand" /> {t("tp_bullets")}
            </h2>
            <ul className="mt-5 space-y-4">
              {c.bullets.map((b, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <ShieldCheck size={17} className="text-magwasabi mt-1 shrink-0" />
                  <p className="text-sm leading-relaxed text-ink/80">{b}</p>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex gap-3 items-start bg-blush/60 border border-magvermilion/15 rounded-2xl p-5">
              <Info size={17} className="text-magvermilion mt-0.5 shrink-0" />
              <div>
                <div className="text-[11px] font-bold tracking-widest uppercase text-magvermilion">{t("tp_note")}</div>
                <p className="mt-1.5 text-sm leading-relaxed text-ink/75">{c.note}</p>
              </div>
            </div>
          </motion.div>

          <motion.aside initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <div className="text-[11px] font-bold tracking-widest uppercase text-sub">{t("guide_recommended")}</div>
            <div className="mt-3 space-y-3" data-testid="tumor-treatments">
              {recommended.map((tr) => {
                const tc = locT(tr, lang);
                const ss = statusStyle[tr.status] || statusStyle.research;
                return (
                  <Link key={tr.id} to={`/tedavi/${tr.id}`} data-testid={`tumor-treat-${tr.id}`}
                    className="block bg-white rounded-2xl border border-black/5 hover:border-brand/30 shadow-sm hover:shadow-lg p-4 transition-all group">
                    <div className="flex gap-3 items-center">
                      <img src={tr.img} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-ink leading-tight group-hover:text-brand transition-colors">{tc.title}</div>
                        <span className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider" style={{ backgroundColor: ss.backgroundColor, color: ss.color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ss.dot }} />
                          {t(`mag_status_${tr.status}`)}
                        </span>
                      </div>
                      <ArrowRight size={15} className="ml-auto text-sub group-hover:text-brand group-hover:translate-x-0.5 transition-all shrink-0" />
                    </div>
                  </Link>
                );
              })}
            </div>
            <a href="/#iletisim" data-testid="tumor-cta"
              className="mt-5 block text-center bg-brand hover:bg-branddeep text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-colors shadow-lg shadow-brand/25">
              {t("hero_cta1")} →
            </a>
            <p className="mt-3 text-[11px] leading-snug text-sub text-center">{t("tp_cta_note")}</p>
          </motion.aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
